import { createGlobalStyle } from "styled-components";

export const Styles = createGlobalStyle`
  :root {
    /* Colour tokens - Good Luck Metals Industrial Chapters */
    --bg: #F4F6F9;
    --surface: #E2E8F0;
    --surface-alt: #CBD5E1;
    --ink: #0F172A;
    --muted: #334155;
    --brand-blue: #1363DF;
    --brand-blue-dark: #0B4BB3;
    --brand-green: #62A82C;
    --brand-green-dark: #3E7217;
    --chapter-dark: #0F172A;
    --chapter-dark-surface: #1E293B;
    --chapter-blue: #1363DF;
    --chapter-green: #1E3A1A;
    --paper: #FFFFFF;

    /* Strict 5-step fluid type scale */
    --step-display: clamp(36px, 5.2vw, 76px);
    --step-head: clamp(28px, 3.5vw, 48px);
    --step-lead: clamp(20px, 1.8vw, 26px);
    --step-body: 17px;
    --step-small: 14px;

    /* Shared layout frame */
    --margin: clamp(16px, 2.5vw, 32px);
    --gutter: clamp(16px, 1.8vw, 24px);
    --max-width: 1376px;
    --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    overflow-x: clip;
  }

  body {
    margin: 0;
    padding: 0;
    background-color: var(--bg);
    color: var(--ink);
    font-family: 'Barlow', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: var(--step-body);
    line-height: 1.58;
    overflow-x: clip;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: -0.015em;
    line-height: 0.96;
    color: inherit;
    margin: 0 0 16px 0;
  }

  h1 {
    font-size: var(--step-display);
    line-height: 0.92;
  }

  h2 {
    font-size: var(--step-head);
    line-height: 0.96;
  }

  h3 {
    font-size: var(--step-lead);
    line-height: 1.08;
  }

  h4, h5, h6 {
    font-size: var(--step-body);
    line-height: 1.2;
  }

  p {
    font-family: 'Barlow', sans-serif;
    font-size: var(--step-body);
    line-height: 1.58;
    color: inherit;
    margin: 0 0 16px 0;
    max-width: 65ch;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.18s var(--ease-out), background-color 0.18s var(--ease-out), transform 0.18s var(--ease-out), opacity 0.18s var(--ease-out);
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: 2px solid var(--brand-blue);
    outline-offset: 2px;
  }

  img, video {
    max-width: 100%;
    display: block;
  }

  /* Layout utility frame */
  .glm-container {
    width: 100%;
    max-width: 100%;
    padding-inline: var(--margin);
    margin-inline: auto;
  }

  /* Chapter blocks - Colour Chapters architecture */
  .glm-chapter {
    width: 100%;
    padding-block: clamp(72px, 8vw, 120px);
    padding-inline: var(--margin);
  }

  .glm-chapter--dark {
    background-color: var(--chapter-dark);
    color: var(--paper);
  }

  .glm-chapter--light {
    background-color: var(--bg);
    color: var(--ink);
  }

  .glm-chapter--surface {
    background-color: var(--surface);
    color: var(--ink);
  }

  .glm-chapter--blue {
    background-color: var(--chapter-blue);
    color: var(--paper);
  }

  .glm-chapter--paper {
    background-color: var(--paper);
    color: var(--ink);
  }

  /* Primary & secondary controls */
  .glm-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 48px;
    padding: 12px 24px;
    font-family: 'Oswald', sans-serif;
    font-size: var(--step-body);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border: none;
    border-radius: 0;
    cursor: pointer;
    text-decoration: none;
    transition: transform 0.18s var(--ease-out), background-color 0.18s var(--ease-out), color 0.18s var(--ease-out), opacity 0.18s var(--ease-out);
  }

  .glm-btn:hover {
    transform: translateY(-2px);
  }

  .glm-btn:active {
    transform: scale(0.98);
  }

  .glm-btn--green {
    background-color: var(--brand-green-dark);
    color: var(--paper);
  }

  .glm-btn--green:hover {
    background-color: #2E5810;
    color: var(--paper);
  }

  .glm-btn--blue {
    background-color: var(--brand-blue);
    color: var(--paper);
  }

  .glm-btn--blue:hover {
    background-color: var(--brand-blue-dark);
    color: var(--paper);
  }

  .glm-btn--dark {
    background-color: var(--chapter-dark);
    color: var(--paper);
  }

  .glm-btn--dark:hover {
    background-color: var(--chapter-dark-surface);
    color: var(--paper);
  }

  .glm-btn--paper {
    background-color: var(--paper);
    color: var(--chapter-dark);
  }

  .glm-btn--paper:hover {
    background-color: var(--surface);
    color: var(--chapter-dark);
  }

  /* Motion layer - active only when user allows motion */
  @media (prefers-reduced-motion: no-preference) {
    .glm-load-line {
      display: block;
      overflow: hidden;
    }

    .glm-load-line > span {
      display: block;
      animation: glmRiseIn 0.72s var(--ease-out) both;
    }

    .glm-load-line:nth-child(2) > span {
      animation-delay: 0.09s;
    }

    .glm-load-fade {
      animation: glmFadeUp 0.65s var(--ease-out) 0.18s both;
    }

    .glm-reveal {
      opacity: 0;
      transform: translateY(18px);
      transition: opacity 0.6s var(--ease-out), transform 0.6s var(--ease-out);
    }

    .glm-reveal.is-visible {
      opacity: 1;
      transform: translateY(0);
    }

    .glm-photo-zoom {
      overflow: hidden;
    }

    .glm-photo-zoom img {
      transition: transform 0.7s var(--ease-out);
    }

    .glm-photo-zoom:hover img {
      transform: scale(1.03);
    }
  }

  @keyframes glmRiseIn {
    from {
      transform: translateY(102%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes glmFadeUp {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;
